import React, { useState, useEffect } from 'react';
import {
    Save, Settings as SettingsIcon, DollarSign, FileText,
    Plus, Trash2, Edit2, Check, Layout, Image as ImageIcon,
    UploadCloud, Type, X, Building2, Percent, Key, Smartphone,
    Users, Moon, Map, Zap, Loader2, Eye, Sparkles, RotateCcw,
    Scale, AlertTriangle, ShieldAlert, Info, Shield, FileArchive } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    SettingsService,
    ActivityTypeService,
    FigureProfessionaliService,
    FilesService
} from '../services/apiService';
import { SettingsDto, ActivityType, FiguraProfessionale, UserRole } from '../types';
import { useToast } from "../components/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import LegalDocumentsAdminView from "./LegalDocumentsAdminView";
import { LogExportModal } from "../components/LogExportModal";
import { pdf } from '@react-pdf/renderer';
import { InformativaPrivacyPDF } from '../components/InformativaPrivacyPDF';
import { stampInformativaPageNumbers } from '../utils/stampPageNumbers';
import NumberInput from '../components/NumberInput';
import ConnessioneTestPanel from '../components/ConnessioneTestPanel';


// Upload e anteprima immagine riutilizzabile
interface ImageSlotProps {
    label: string;
    hint?: string;
    imageUrl?: string;
    isUploading: boolean;
    onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemove: () => void;
    accentColor?: 'blue' | 'amber';
}
const ImageSlot: React.FC<ImageSlotProps> = ({
                                                 label, hint, imageUrl, isUploading, onUpload, onRemove, accentColor = 'blue'
                                             }) => {
    const accent = accentColor === 'amber'
        ? { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-600', hover: 'hover:text-amber-600' }
        : { border: 'border-blue-200',  bg: 'bg-blue-50',  text: 'text-blue-600',  hover: 'hover:text-blue-600' };
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-sm">
            <div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
                {hint && <p className="text-[9px] text-slate-400 mt-0.5">{hint}</p>}
            </div>
            <div className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed ${imageUrl ? 'border-slate-200 p-2' : `${accent.border} ${accent.bg} p-8`} transition-all`}>
                {imageUrl ? (
                    <div className="relative group w-full">
                        <img src={imageUrl} alt={label} className="w-full max-h-28 object-contain rounded-lg border border-slate-100 bg-white shadow-sm"/>
                        <button
                            onClick={onRemove}
                            title="Rimuovi immagine"
                            aria-label="Rimuovi immagine"
                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        ><X size={13}/></button>
                        <label className={`mt-2 flex items-center justify-center gap-1.5 text-[10px] font-bold ${accent.text} cursor-pointer ${accent.hover} transition-colors`}>
                            <UploadCloud size={12}/> Sostituisci
                            <input type="file" className="hidden" accept="image/*" onChange={onUpload}/>
                        </label>
                    </div>
                ) : (
                    <label className={`flex flex-col items-center cursor-pointer ${accent.hover} transition-colors`}>
                        <div className="p-3 bg-white rounded-full shadow-sm mb-2 text-slate-300">
                            {isUploading ? <Loader2 className="animate-spin" size={28}/> : <UploadCloud size={28}/>}
                        </div>
                        <span className={`font-bold text-xs ${accent.text}`}>Carica immagine</span>
                        <span className="text-[9px] text-slate-400 mt-0.5">{hint || 'PNG o JPG'}</span>
                        <input type="file" className="hidden" accept="image/*" onChange={onUpload}/>
                    </label>
                )}
            </div>
        </div>
    );
};

export const ImpostazioniView: React.FC = () => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === UserRole.ADMIN;
    const [activeTab, setActiveTab] = useState<'generale' | 'listino' | 'figure' | 'stampe' | 'legali'>('generale');

    // Modale export ZIP dei log.
    const [showLogExportModal, setShowLogExportModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState<'header' | 'footer' | 'mandato-header' | 'mandato-footer' | 'privacy-policy' | 'timbro' | null>(null);

    // Anteprima Informativa Privacy generata (modale + blob URL)
    const [privacyPreviewUrl, setPrivacyPreviewUrl] = useState<string | null>(null);
    const [privacyPreviewLoading, setPrivacyPreviewLoading] = useState(false);

    // Modale informativa su cosa fa il software con i dati.
    const [showGdprInfo, setShowGdprInfo] = useState(false);

    // Figure professionali
    const [figure, setFigure] = useState<FiguraProfessionale[]>([]);
    const [editingFigura, setEditingFigura] = useState<FiguraProfessionale | null>(null);

    // Parametri generali e campi stampe
    const [settings, setSettings] = useState<SettingsDto>({
        priceKM: 0,
        hourlyRate: 0,
        hourlyRateFestivo: 0,
        hourlyRateNotturno: 0,
        costSetupPractice: 0,

        majDistance50100: 15,
        majDistance100150: 25,
        majDistance150250: 50,
        majDistance250Plus: 75,
        majNight: 50,
        majHoliday: 50,
        majNightHoliday: 100,
        orarioNotturnoInizio: '22:00',
        orarioNotturnoFine: '06:00',
        majUrgency48: 30,
        majUrgency24: 50,
        majUrgency12: 100,
        majReport48: 15,
        majReport24: 30,

        enableProforma: false,
        openAiApiKey: '',
        otpServiceApiKey: '',
        otpServiceEmail: '',
        otpServicePassword: '',

        agencyName: '',
        address: '',
        city: '',
        phone: '',
        email: '',
        vatNumber: '',
        website: '',
        pec: '',
        licenzaPrefettizia: '',
        emailDpo: '',

        // Coordinate bancarie per il PDF proforma
        iban: '',
        intestatario: '',
        bancaNome: '',

        // Campi stampe
        headerType: 'TEXT',
        footerType: 'TEXT',
        headerReportInvestigativo: '',
        footerReportInvestigativo: '',
        headerImageUrl: '',
        footerImageUrl: '',
        mandatoHeaderImageUrl: '',
        mandatoFooterImageUrl: '',
        privacyPolicyMode: 'UPLOAD',

        // Due soglie separate per l'allerta "Orari difformi": turni fiduciari e
        // incarichi investigativi (opzionale).
        sogliaOrariDifformiMin: undefined,
        sogliaOrariDifformiIncarichiMin: undefined,
        // Default false: il responsabile deve chiedere approvazione.
        responsabilePuoEliminareMediaDiretto: false,
        // Default false: buoni pasto automatici nascosti al collaboratore.
        buoniPastoVisibiliCollaboratori: false,
        // Default false: nessuna anonimizzazione automatica dei dati cestinati finché
        // il titolare non la abilita.
        gdprAutoAnonymizationEnabled: false,
        gdprRetentionDays: undefined,
        gdprUserRetentionDays: undefined,
        gdprHardDeleteDaysAfterAnonymization: undefined,
        // undefined = default del backend (200MB).
        dedupVideoMaxMb: undefined,
    });

    const [activities, setActivities] = useState<ActivityType[]>([]);
    const [editingActivity, setEditingActivity] = useState<ActivityType | null>(null);

    useEffect(() => {
        loadAllData();
    }, []);

    const loadAllData = async () => {
        setIsLoading(true);
        try {
            const [settingsRes, activitiesRes, figureRes] = await Promise.all([
                SettingsService.getAll(),
                ActivityTypeService.getAll(),
                FigureProfessionaliService.getAll().catch(() => ({ success: true, data: [] }))
            ]);

            if (settingsRes.success && settingsRes.data.length > 0) {
                const s = settingsRes.data[0];
                setSettings(prev => ({
                    ...prev,
                    ...s,
                    // Default per i campi nuovi quando nel DB sono null
                    headerType: s.headerType || 'TEXT',
                    footerType: s.footerType || 'TEXT',
                    mandatoHeaderImageUrl: s.mandatoHeaderUrl || '',
                    mandatoFooterImageUrl: s.mandatoFooterUrl || '',
                    headerReportInvestigativo: s.headerReportInvestigativo || '',
                    footerReportInvestigativo: s.footerReportInvestigativo || ''
                }));
            }
            if (activitiesRes.success) setActivities(activitiesRes.data);
            if (figureRes && figureRes.success) setFigure(figureRes.data);

        } catch (error) {
            addToast("Errore caricamento dati", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            if (settings.id) {
                await SettingsService.update(settings.id, settings);
            } else {
                const res = await SettingsService.create(settings);
                if (res.data?.id) setSettings(prev => ({ ...prev, id: res.data.id }));
            }
            // Il secret non torna dal server: si aggiorna solo lo stato "impostato".
            setSettings(prev => ({
                ...prev,
                otpServiceWebhookSecretSet: prev.otpServiceWebhookSecretRimuovi ? false
                    : (prev.otpServiceWebhookSecret ? true : prev.otpServiceWebhookSecretSet),
                otpServiceWebhookSecret: '',
                otpServiceWebhookSecretRimuovi: false,
            }));
            addToast("Impostazioni salvate con successo", "success");
        } catch (error) {
            addToast("Errore salvataggio parametri", "error");
        } finally {
            setIsSaving(false);
        }
    };

    // Carica nel form i default definiti nel BE, che resta l'unica fonte. Non salva:
    // l'admin rivede e poi salva.
    const [loadingAiDefaults, setLoadingAiDefaults] = useState(false);
    const handleLoadAiDefaults = async () => {
        setLoadingAiDefaults(true);
        try {
            const res = await SettingsService.getAiDefaults();
            const d: any = res.data || {};
            setSettings(prev => ({
                ...prev,
                openAiModel: d.openAiModel ?? prev.openAiModel,
                openAiTemperature: d.openAiTemperature ?? undefined,
                openAiTopP: d.openAiTopP ?? undefined,
                openAiPresencePenalty: d.openAiPresencePenalty ?? undefined,
                openAiFrequencyPenalty: d.openAiFrequencyPenalty ?? undefined,
                openAiMaxTokens: d.openAiMaxTokens ?? undefined,
                openAiSystemPrompt: d.openAiSystemPrompt ?? '',
                openAiDeveloperPrompt: d.openAiDeveloperPrompt ?? '',
            }));
            addToast("Valori predefiniti caricati. Ricorda di salvare.", "success");
        } catch {
            addToast("Errore caricamento valori predefiniti IA", "error");
        } finally {
            setLoadingAiDefaults(false);
        }
    };

    // Upload immagini header / footer
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, target: 'header' | 'footer' | 'mandato-header' | 'mandato-footer' | 'timbro') => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(target);
        try {
            // Endpoint dedicati di Settings, che salvano la key S3 nel DB
            let updatedSettings: any;
            if (target === 'mandato-header' || target === 'mandato-footer') {
                const res = await SettingsService.uploadMandatoImage(file, target);
                updatedSettings = res.data || res;
            } else if (target === 'timbro') {
                const res = await SettingsService.uploadTimbro(file);
                updatedSettings = res.data || res;
            } else {
                const res = await SettingsService.uploadBrandingImage(file, target as 'header' | 'footer');
                updatedSettings = res.data || res;
            }

            // Aggiorna lo stato con gli URL restituiti dal backend
            if (updatedSettings) {
                setSettings(prev => ({
                    ...prev,
                    logoUrl:          updatedSettings.logoUrl          ?? prev.logoUrl,
                    reportHeaderUrl:  updatedSettings.reportHeaderUrl  ?? prev.reportHeaderUrl,
                    reportFooterUrl:  updatedSettings.reportFooterUrl  ?? prev.reportFooterUrl,
                    mandatoHeaderUrl: updatedSettings.mandatoHeaderUrl ?? (prev as any).mandatoHeaderUrl,
                    mandatoFooterUrl: updatedSettings.mandatoFooterUrl ?? (prev as any).mandatoFooterUrl,
                    timbroUrl:        updatedSettings.timbroUrl        ?? (prev as any).timbroUrl,
                    // URL locali per l'anteprima immediata
                    headerImageUrl:   target === 'header' ? (updatedSettings.reportHeaderUrl || prev.headerImageUrl) : prev.headerImageUrl,
                    footerImageUrl:   target === 'footer' ? (updatedSettings.reportFooterUrl || prev.footerImageUrl) : prev.footerImageUrl,
                    mandatoHeaderImageUrl: target === 'mandato-header' ? (updatedSettings.mandatoHeaderUrl || (prev as any).mandatoHeaderImageUrl) : (prev as any).mandatoHeaderImageUrl,
                    mandatoFooterImageUrl: target === 'mandato-footer' ? (updatedSettings.mandatoFooterUrl || (prev as any).mandatoFooterImageUrl) : (prev as any).mandatoFooterImageUrl,
                }));
            }
            addToast('Immagine caricata e salvata correttamente', 'success');
        } catch (error) {
            addToast("Errore durante l'upload dell'immagine", 'error');
        } finally {
            setIsUploading(null);
            e.target.value = '';
        }
    };

    // Rimozione immagine: il BE azzera la chiave e cancella il file; qui si svuotano tutti gli
    // stati che la mostrano (URL dal BE e copie per l'anteprima).
    const handleImageRemove = async (target: 'header' | 'footer' | 'mandato-header' | 'timbro') => {
        if (!window.confirm("Rimuovere l'immagine? I documenti successivi non la useranno più.")) return;
        setIsUploading(target);
        try {
            await SettingsService.removeBrandingImage(target);
            setSettings(prev => {
                const next: any = { ...prev };
                if (target === 'header') { next.reportHeaderUrl = ''; next.headerImageUrl = ''; }
                else if (target === 'footer') { next.reportFooterUrl = ''; next.footerImageUrl = ''; }
                else if (target === 'mandato-header') { next.mandatoHeaderUrl = ''; next.mandatoHeaderImageUrl = ''; }
                else if (target === 'timbro') { next.timbroUrl = ''; }
                return next;
            });
            addToast('Immagine rimossa', 'success');
        } catch {
            addToast("Errore durante la rimozione dell'immagine", 'error');
        } finally {
            setIsUploading(null);
        }
    };

    // Anteprima del PDF generato dalla piattaforma
    const handlePreviewGeneratedPrivacy = async () => {
        setPrivacyPreviewLoading(true);
        try {
            const blob = await stampInformativaPageNumbers(
                await pdf(<InformativaPrivacyPDF settings={settings as any} />).toBlob()
            );
            const url = URL.createObjectURL(blob);
            // Revoca l'URL dell'anteprima precedente per evitare leak
            if (privacyPreviewUrl) URL.revokeObjectURL(privacyPreviewUrl);
            setPrivacyPreviewUrl(url);
        } catch (e) {
            console.error(e);
            addToast("Errore generazione anteprima Informativa Privacy", "error");
        } finally {
            setPrivacyPreviewLoading(false);
        }
    };

    const closePrivacyPreview = () => {
        if (privacyPreviewUrl) URL.revokeObjectURL(privacyPreviewUrl);
        setPrivacyPreviewUrl(null);
    };

    const downloadPrivacyPreview = () => {
        if (!privacyPreviewUrl) return;
        const link = document.createElement('a');
        link.href = privacyPreviewUrl;
        link.download = `Informativa_Privacy_${(settings.agencyName || 'Agenzia').replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Cleanup blob URL allo smontaggio
    useEffect(() => () => {
        if (privacyPreviewUrl) URL.revokeObjectURL(privacyPreviewUrl);
    }, [privacyPreviewUrl]);

    // Upload PDF informativa privacy
    const handlePrivacyUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            addToast('Carica un file PDF valido', 'error');
            return;
        }
        setIsUploading('privacy-policy');
        try {
            const res = await SettingsService.uploadPrivacyPolicy(file);
            const updated: any = (res as any).data || res;
            if (updated?.privacyPolicyUrl) {
                setSettings(prev => ({ ...prev, privacyPolicyUrl: updated.privacyPolicyUrl }));
            }
            addToast('Informativa Privacy caricata con successo', 'success');
        } catch (error) {
            addToast("Errore durante l'upload dell'Informativa Privacy", 'error');
        } finally {
            setIsUploading(null);
            e.target.value = '';
        }
    };

    // Listino
    const handleSaveActivity = async () => {
        if (!editingActivity || !editingActivity.name) return;
        try {
            if (editingActivity.id) {
                await ActivityTypeService.update(editingActivity.id, editingActivity);
                setActivities(prev => prev.map(a => a.id === editingActivity.id ? editingActivity : a));
            } else {
                const res = await ActivityTypeService.create(editingActivity);
                const newAct = (res as any).data || res;
                setActivities(prev => [...prev, newAct]);
            }
            setEditingActivity(null);
            addToast("Voce listino salvata", "success");
        } catch (e) { addToast("Errore salvataggio", "error"); }
    };

    const handleDeleteActivity = async (id: number) => {
        if (!window.confirm("Eliminare questa voce?")) return;
        try {
            await ActivityTypeService.delete(id);
            setActivities(prev => prev.filter(a => a.id !== id));
            addToast("Voce eliminata", "success");
        } catch (e) { addToast("Errore eliminazione", "error"); }
    };

    // Figure
    const handleSaveFigura = async () => {
        if (!editingFigura || !editingFigura.nome) return addToast("Nome obbligatorio", "error");
        try {
            if (editingFigura.id) {
                await FigureProfessionaliService.update(editingFigura.id, editingFigura);
                setFigure(prev => prev.map(f => f.id === editingFigura.id ? editingFigura : f));
            } else {
                const res = await FigureProfessionaliService.create(editingFigura);
                const newFig = (res as any).data || res;
                setFigure(prev => [...prev, newFig]);
            }
            setEditingFigura(null);
            addToast("Figura salvata", "success");
        } catch (e) { addToast("Errore salvataggio", "error"); }
    };

    const handleDeleteFigura = async (id: number) => {
        if (!window.confirm("Eliminare questa figura?")) return;
        try {
            await FigureProfessionaliService.delete(id);
            setFigure(prev => prev.filter(f => f.id !== id));
            addToast("Figura eliminata", "success");
        } catch (e) { addToast("Errore eliminazione", "error"); }
    };

    if (isLoading) return <div className="flex h-screen items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-blue-600" size={40}/></div>;

    return (
        <div className="p-6 max-w-6xl mx-auto pb-24 space-y-6">

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg">
                        <SettingsIcon size={24}/>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Impostazioni Sistema</h1>
                        <p className="text-sm text-gray-500">Configura parametri economici, API e layout documenti.</p>
                    </div>
                </div>

                <div className="flex flex-col gap-3 w-full md:w-auto md:items-end">
                    {/* Registro audit ed export log: solo ADMIN, i log sono globali e contengono dati personali. */}
                    {isAdmin && (
                        <div className="flex gap-2 flex-wrap md:justify-end">
                            <button
                                onClick={() => navigate('/amministrazione/audit')}
                                className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-100 shadow-sm flex items-center gap-2 transition-colors"
                            >
                                <Shield size={16}/> Registro Audit (Log)
                            </button>
                            <button
                                onClick={() => setShowLogExportModal(true)}
                                className="bg-slate-800 text-white border border-slate-800 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-900 shadow-sm flex items-center gap-2 transition-colors"
                                title="Scarica log di sistema in ZIP con date custom, categorie a scelta e PII redatte"
                            >
                                <FileArchive size={16}/> Esporta Log (ZIP)
                            </button>
                        </div>
                    )}

                    <div className="flex bg-gray-100 p-1 rounded-xl shadow-inner overflow-x-auto w-full md:w-auto">
                        <button onClick={() => setActiveTab('generale')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'generale' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Generale e API</button>
                        <button onClick={() => setActiveTab('stampe')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'stampe' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Documenti e Stampe</button>
                        <button onClick={() => setActiveTab('figure')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'figure' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Figure Professionali</button>
                        <button onClick={() => setActiveTab('listino')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'listino' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Listino Servizi</button>
                        {isAdmin && (
                            <button onClick={() => setActiveTab('legali')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'legali' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Documenti Legali</button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modale export ZIP dei log. */}
            {showLogExportModal && (
                <LogExportModal onClose={() => setShowLogExportModal(false)}/>
            )}

            {/* Tab documenti legali (solo ADMIN) */}
            {activeTab === 'legali' && isAdmin && (
                <div className="animate-in fade-in slide-in-from-left-4">
                    <LegalDocumentsAdminView />
                </div>
            )}

            {/* Tab generale */}
            {activeTab === 'generale' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in slide-in-from-left-4">
                    {/* Anagrafica agenzia */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-5">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg">
                            <Building2 size={20} className="text-blue-600"/> Dati Agenzia (Per Intestazioni)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                            <div className="lg:col-span-3">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ragione Sociale</label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={settings.agencyName} onChange={e => setSettings({...settings, agencyName: e.target.value})} />
                            </div>
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Indirizzo</label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.address} onChange={e => setSettings({...settings, address: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Città / CAP</label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.city} onChange={e => setSettings({...settings, city: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">P.IVA / CF</label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono" value={settings.vatNumber} onChange={e => setSettings({...settings, vatNumber: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                                <input type="email" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.email} onChange={e => setSettings({...settings, email: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Telefono</label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.phone} onChange={e => setSettings({...settings, phone: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sito web <span className="text-slate-400 normal-case font-medium">· facoltativo</span></label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="es. www.agenzia.it" value={settings.website || ''} onChange={e => setSettings({...settings, website: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">PEC <span className="text-slate-400 normal-case font-medium">· documenti legali</span></label>
                                <input type="email" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.pec || ''} onChange={e => setSettings({...settings, pec: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Licenza prefettizia <span className="text-slate-400 normal-case font-medium">· art. 134 TULPS</span></label>
                                <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.licenzaPrefettizia || ''} onChange={e => setSettings({...settings, licenzaPrefettizia: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email DPO <span className="text-slate-400 normal-case font-medium">· documenti legali · facoltativa</span></label>
                                <input type="email" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={settings.emailDpo || ''} onChange={e => setSettings({...settings, emailDpo: e.target.value})} />
                            </div>

                            {/* Coordinate bancarie per "Modalità di pagamento" del PDF proforma. */}
                            <div className="lg:col-span-3 mt-2 pt-4 border-t border-slate-100">
                                <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-3">
                                    Coordinate bancarie (PDF proforma)
                                </p>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Intestatario</label>
                                        <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Es. IURIS INVESTIGAZIONI di Giovanni Iuris"
                                            value={(settings as any).intestatario || ''}
                                            onChange={e => setSettings({...settings, intestatario: e.target.value} as any)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Banca</label>
                                        <input type="text" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Es. Intesa Sanpaolo"
                                            value={(settings as any).bancaNome || ''}
                                            onChange={e => setSettings({...settings, bancaNome: e.target.value} as any)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                            IBAN
                                            {(settings as any).ibanSet && !(settings as any).iban && (
                                                <span className="ml-2 text-emerald-600 normal-case font-normal">🔒 impostato — reinserisci per sostituire</span>
                                            )}
                                        </label>
                                        <input type="text" maxLength={34} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono uppercase"
                                            placeholder={(settings as any).ibanSet ? "•••• •••• •••• •••• •••• •••• •••" : "IT00 X000 0000 0000 0000 0000 000"}
                                            value={(settings as any).iban || ''}
                                            onChange={e => setSettings({...settings, iban: e.target.value.toUpperCase()} as any)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Parametri preventivo */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg mb-4">
                            <DollarSign size={20} className="text-emerald-600"/> Parametri Preventivo
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Rimborso chilometrico (€/km)</label>
                                <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                    <NumberInput
                                        min={0}
                                        step="0.01"
                                        className="flex-1 p-2 border rounded text-right text-sm font-bold bg-white"
                                        value={settings.priceKM ?? 0}
                                        onChange={e => setSettings({...settings, priceKM: parseFloat(e.target.value) || 0})}
                                    />
                                    <span className="text-xs text-slate-400 font-bold">€/km</span>
                                </div>
                                <p className="text-[10px] text-slate-500 italic mt-1.5">Usato come tariffa predefinita per la voce "Rimborso chilometrico" nei nuovi preventivi.</p>
                            </div>
                        </div>
                    </div>

                    {/* Maggiorazioni % */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg mb-4">
                            <Percent size={20} className="text-amber-500"/> Maggiorazioni Standard (%)
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Distanze */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Distanza Sede</label>
                                {[
                                    { l: '50-100 km', f: 'majDistance50100' },
                                    { l: '100-150 km', f: 'majDistance100150' },
                                    { l: '150-250 km', f: 'majDistance150250' },
                                    // Fascia oltre 250 km (default 75%).
                                    { l: 'Oltre 250 km', f: 'majDistance250Plus' }
                                ].map(i => (
                                    <div key={i.f} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                        <span className="flex-1 text-xs font-bold text-slate-600">{i.l}</span>
                                        <NumberInput
                                               min={0} className="w-16 p-1 border rounded text-right text-sm font-bold" value={(settings as any)[i.f]} onChange={e => setSettings({...settings, [i.f]: parseFloat(e.target.value) || 0})} />
                                        <span className="text-xs text-slate-400 font-bold">%</span>
                                    </div>
                                ))}
                            </div>
                            {/* Orari */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Orari e Festivi</label>
                                {[
                                    { l: 'Notturno', f: 'majNight' },
                                    { l: 'Festivo', f: 'majHoliday' },
                                    { l: 'Notte/Festivo', f: 'majNightHoliday' }
                                ].map(i => (
                                    <div key={i.f} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                        <span className="flex-1 text-xs font-bold text-slate-600">{i.l}</span>
                                        <NumberInput
                                               min={0} className="w-16 p-1 border rounded text-right text-sm font-bold" value={(settings as any)[i.f]} onChange={e => setSettings({...settings, [i.f]: parseFloat(e.target.value) || 0})} />
                                        <span className="text-xs text-slate-400 font-bold">%</span>
                                    </div>
                                ))}

                                {/* Definizione fascia oraria notturna */}
                                <div className="bg-indigo-50 p-2.5 rounded-lg border border-indigo-100 mt-3">
                                    <p className="text-[9px] font-black text-indigo-700 uppercase tracking-widest mb-2">Fascia notturna</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Dalle</label>
                                            <input
                                                type="time"
                                                className="w-full p-1.5 border rounded text-sm font-bold bg-white"
                                                value={settings.orarioNotturnoInizio || ''}
                                                onChange={e => setSettings({...settings, orarioNotturnoInizio: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Alle</label>
                                            <input
                                                type="time"
                                                className="w-full p-1.5 border rounded text-sm font-bold bg-white"
                                                value={settings.orarioNotturnoFine || ''}
                                                onChange={e => setSettings({...settings, orarioNotturnoFine: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-[9px] text-slate-500 italic mt-1.5">Le voci che ricadono in questa fascia ricevono automaticamente la maggiorazione "Notturno" nel preventivo.</p>
                                </div>
                            </div>
                            {/* Urgenza */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Urgenza Attivazione</label>
                                {[
                                    { l: 'Entro 48h', f: 'majUrgency48' },
                                    { l: 'Entro 24h', f: 'majUrgency24' },
                                    { l: 'Entro 12h', f: 'majUrgency12' }
                                ].map(i => (
                                    <div key={i.f} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                        <span className="flex-1 text-xs font-bold text-slate-600">{i.l}</span>
                                        <NumberInput
                                               min={0} className="w-16 p-1 border rounded text-right text-sm font-bold" value={(settings as any)[i.f]} onChange={e => setSettings({...settings, [i.f]: parseFloat(e.target.value) || 0})} />
                                        <span className="text-xs text-slate-400 font-bold">%</span>
                                    </div>
                                ))}
                            </div>
                            {/* Relazione */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Urgenza Relazione</label>
                                {[
                                    { l: 'Entro 48h', f: 'majReport48' },
                                    { l: 'Entro 24h', f: 'majReport24' }
                                ].map(i => (
                                    <div key={i.f} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100">
                                        <span className="flex-1 text-xs font-bold text-slate-600">{i.l}</span>
                                        <NumberInput
                                               min={0} className="w-16 p-1 border rounded text-right text-sm font-bold" value={(settings as any)[i.f]} onChange={e => setSettings({...settings, [i.f]: parseFloat(e.target.value) || 0})} />
                                        <span className="text-xs text-slate-400 font-bold">%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Integrazioni API */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-6">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg">
                            <Key size={20} className="text-indigo-600"/> Chiavi API e Servizi
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/*
                              OpenAI: chiave write-only cifrata. Il BE non la restituisce mai,
                              `openAiApiKeySet` dice solo se è già presente.
                            */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                    OpenAI (IA)
                                    {(settings as any).openAiApiKeySet && !settings.openAiApiKey && (
                                        <span className="text-xs text-emerald-600 font-normal">🔒 impostata</span>
                                    )}
                                </h4>
                                <input type="password"
                                    placeholder={(settings as any).openAiApiKeySet ? "•••••••••••••• (reinserisci per sostituire)" : "sk-..."}
                                    className="w-full p-2.5 border rounded-lg font-mono text-sm"
                                    value={settings.openAiApiKey || ''}
                                    onChange={e => setSettings({...settings, openAiApiKey: e.target.value})} />
                            </div>
                            {/*
                              OTP Service: l'autenticazione usa email + password. Il campo API Key resta
                              nel modello ma è nascosto perché oggi non serve. Email e password sono
                              write-only; i flag `otpServiceEmailSet`/`otpServicePasswordSet` dicono se sono già salvate.
                            */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                    OTP Service (Firma Digitale)
                                    {((settings as any).otpServiceEmailSet || (settings as any).otpServicePasswordSet) &&
                                     !settings.otpServiceEmail && !settings.otpServicePassword && (
                                        <span className="text-xs text-emerald-600 font-normal">🔒 credenziali impostate</span>
                                    )}
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <input type="text"
                                            placeholder={(settings as any).otpServiceEmailSet ? "••••••@•••••••" : "Email"}
                                            className="flex-1 p-2.5 border rounded-lg text-sm"
                                            value={settings.otpServiceEmail || ''}
                                            onChange={e => setSettings({...settings, otpServiceEmail: e.target.value})} />
                                        <input type="password"
                                            placeholder={(settings as any).otpServicePasswordSet ? "••••••••••••" : "Password"}
                                            className="flex-1 p-2.5 border rounded-lg text-sm"
                                            value={settings.otpServicePassword || ''}
                                            onChange={e => setSettings({...settings, otpServicePassword: e.target.value})} />
                                    </div>
                                    {((settings as any).otpServiceEmailSet || (settings as any).otpServicePasswordSet) && (
                                        <p className="text-xs text-slate-500 italic">
                                            Lascia i campi vuoti per mantenere i valori attuali; compila solo se vuoi sostituirli.
                                        </p>
                                    )}
                                    <div className="pt-1 space-y-1">
                                        <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                                            Webhook secret (facoltativo)
                                            {settings.otpServiceWebhookSecretSet && !settings.otpServiceWebhookSecret && !settings.otpServiceWebhookSecretRimuovi && (
                                                <span className="text-emerald-600 font-normal">🔒 impostato</span>
                                            )}
                                            {settings.otpServiceWebhookSecretRimuovi && (
                                                <span className="text-amber-600 font-normal">verrà rimosso al salvataggio</span>
                                            )}
                                        </label>
                                        <div className="flex gap-2">
                                            <input type="password" autoComplete="new-password"
                                                placeholder={settings.otpServiceWebhookSecretSet ? "••••••••••••" : "Secret dei webhook"}
                                                className="flex-1 p-2.5 border rounded-lg text-sm"
                                                value={settings.otpServiceWebhookSecret || ''}
                                                onChange={e => setSettings({...settings, otpServiceWebhookSecret: e.target.value, otpServiceWebhookSecretRimuovi: false})} />
                                            {settings.otpServiceWebhookSecretSet && (
                                                <button type="button"
                                                    onClick={() => setSettings({...settings, otpServiceWebhookSecret: '', otpServiceWebhookSecretRimuovi: !settings.otpServiceWebhookSecretRimuovi})}
                                                    className="shrink-0 px-3 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-600 hover:bg-slate-50">
                                                    {settings.otpServiceWebhookSecretRimuovi ? 'Annulla' : 'Rimuovi'}
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Compilalo solo se il secret è attivo anche su OTP Service: con un secret diverso gli
                                            aggiornamenti delle firme vengono rifiutati. Vuoto, gli aggiornamenti si accettano come sempre.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/*
                      Configurazione IA "Migliora relazione": modello, temperatura, parametri e
                      prompt; vuoto = default.
                    */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                        <div className="flex items-center justify-between border-b pb-3 gap-3">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2 text-lg">
                                <Sparkles size={20} className="text-indigo-600"/> Configurazione IA — "Migliora relazione"
                            </h3>
                            <button type="button" onClick={handleLoadAiDefaults} disabled={loadingAiDefaults}
                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50">
                                {loadingAiDefaults ? <Loader2 size={14} className="animate-spin"/> : <RotateCcw size={14}/>}
                                Ripristina predefiniti
                            </button>
                        </div>
                        <p className="text-xs text-slate-500">
                            Parametri della chiamata OpenAI usata per riscrivere le relazioni. Lascia un campo vuoto per usare il valore di default;
                            oppure premi <span className="font-semibold">"Ripristina predefiniti"</span> per caricare nel form il prompt completo e tutti i parametri cablati, così puoi vederli e modificarli.
                        </p>
                        <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            Nota: con i modelli "reasoning" (serie <span className="font-mono">o1/o3/o4</span>, <span className="font-mono">gpt-5</span>) temperatura, top_p e penalties vengono ignorati e il limite usa automaticamente <span className="font-mono">max_completion_tokens</span>. Con i modelli GPT standard (es. <span className="font-mono">gpt-4o-mini</span>) valgono tutti i parametri.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Modello</label>
                                <input type="text" placeholder="gpt-4o-mini" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiModel || ''}
                                    onChange={e => setSettings({...settings, openAiModel: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Temperature (0–2)</label>
                                <NumberInput min={0} max={2} step="0.1" placeholder="0.2" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiTemperature ?? ''}
                                    onChange={e => setSettings({...settings, openAiTemperature: e.target.value === '' ? undefined : parseFloat(e.target.value)})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Max tokens</label>
                                <NumberInput min={1} step="1" placeholder="auto" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiMaxTokens ?? ''}
                                    onChange={e => setSettings({...settings, openAiMaxTokens: e.target.value === '' ? undefined : Math.max(1, parseInt(e.target.value, 10) || 0)})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Top P (0–1)</label>
                                <NumberInput min={0} max={1} step="0.05" placeholder="1.0" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiTopP ?? ''}
                                    onChange={e => setSettings({...settings, openAiTopP: e.target.value === '' ? undefined : parseFloat(e.target.value)})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Presence penalty (−2…2)</label>
                                <NumberInput min={-2} max={2} step="0.1" placeholder="0" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiPresencePenalty ?? ''}
                                    onChange={e => setSettings({...settings, openAiPresencePenalty: e.target.value === '' ? undefined : parseFloat(e.target.value)})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Frequency penalty (−2…2)</label>
                                <NumberInput min={-2} max={2} step="0.1" placeholder="0" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500"
                                    value={settings.openAiFrequencyPenalty ?? ''}
                                    onChange={e => setSettings({...settings, openAiFrequencyPenalty: e.target.value === '' ? undefined : parseFloat(e.target.value)})} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Prompt di sistema (ruolo del modello)</label>
                            <textarea rows={3} placeholder="Default: redattore di relazioni investigative…" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                value={settings.openAiSystemPrompt || ''}
                                onChange={e => setSettings({...settings, openAiSystemPrompt: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Prompt stile / istruzioni</label>
                            <textarea rows={6} placeholder="Default: stile obbligatorio, terminologia, prudenza probatoria, chiusura 'Fine servizio.'…" className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                value={settings.openAiDeveloperPrompt || ''}
                                onChange={e => setSettings({...settings, openAiDeveloperPrompt: e.target.value})} />
                            <p className="text-[11px] text-slate-400 mt-1">Le istruzioni aggiuntive scritte al momento della generazione si aggiungono comunque a questo prompt.</p>
                        </div>
                    </div>

                    {/*
                      Soglia allerta orari difformi (pianificato vs reale). Solo segnalazione
                      in dashboard e sulla card incarico; vuoto o 0 = nessuna allerta.
                    */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg">
                            <AlertTriangle size={20} className="text-rose-500"/> Allerte Operative
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-1">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                    Soglia turni fiduciari (min)
                                </label>
                                <NumberInput
                                    min={0}
                                    step={1}
                                    placeholder="Es. 30"
                                    className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                    value={settings.sogliaOrariDifformiMin ?? ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setSettings({
                                            ...settings,
                                            sogliaOrariDifformiMin: v === '' ? undefined : Math.max(0, parseInt(v, 10) || 0),
                                        });
                                    }}
                                />
                                <p className="text-[10px] text-slate-500 italic mt-1.5">
                                    Mostra il banner "⚠ Orari difformi" sui turni di vigilanza quando la
                                    differenza tra orario pianificato e reale supera la soglia.
                                    Lascia vuoto per disattivare l'allerta sui turni. Non blocca alcuna operazione.
                                </p>
                            </div>
                            {/*
                              Soglia separata per gli incarichi investigativi, disattivata di default
                              perché gli orari di un'indagine sono spesso flessibili.
                            */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                    Soglia incarichi investigativi (min)
                                </label>
                                <NumberInput
                                    min={0}
                                    step={1}
                                    placeholder="vuoto = disattivata"
                                    className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                    value={settings.sogliaOrariDifformiIncarichiMin ?? ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setSettings({
                                            ...settings,
                                            sogliaOrariDifformiIncarichiMin: v === '' ? undefined : Math.max(0, parseInt(v, 10) || 0),
                                        });
                                    }}
                                />
                                <p className="text-[10px] text-slate-500 italic mt-1.5">
                                    Attivala se vuoi vedere l'allerta anche sugli incarichi investigativi.
                                    Lascia vuoto per disattivarla (default): l'orario di un'indagine è
                                    spesso meno rigido di un turno di vigilanza.
                                </p>
                            </div>
                            {/*
                              Tolleranza sulla data EXIF delle foto start/stop. È bloccante: con EXIF
                              assente o fuori tolleranza il backend rifiuta l'upload (422).
                            */}
                            <div className="md:col-span-2 border-t border-slate-100 pt-4">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                    Tolleranza EXIF foto start/stop (min)
                                </label>
                                <NumberInput
                                    min={0}
                                    step={1}
                                    placeholder="Default 15"
                                    className="w-full md:w-1/2 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                    value={settings.tolleranzaExifFotoStartStopMin ?? ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setSettings({
                                            ...settings,
                                            tolleranzaExifFotoStartStopMin: v === '' ? undefined : Math.max(0, parseInt(v, 10) || 0),
                                        });
                                    }}
                                />
                                <p className="text-[10px] text-slate-500 italic mt-1.5">
                                    Differenza massima ammessa fra l'ora di scatto della foto
                                    (EXIF DateTimeOriginal) e il momento dello start/stop di
                                    turno o foto km. Foto senza EXIF o oltre tolleranza vengono
                                    rifiutate: il collaboratore deve scattarla con la fotocamera
                                    al momento dell'azione, non sceglierla dalla galleria.
                                    Vuoto = default 15. 0 = check disattivato.
                                </p>
                            </div>
                            {/*
                              Soglia dimensione per il controllo duplicati dei video (le foto si controllano
                              sempre). L'hash è in streaming ma su file enormi rallenta l'upload.
                            */}
                            <div className="md:col-span-2 border-t border-slate-100 pt-4">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                    Controllo duplicati video — dimensione massima
                                </label>
                                <select
                                    className="w-full md:w-1/2 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    value={settings.dedupVideoMaxMb ?? ''}
                                    onChange={e => {
                                        const v = e.target.value;
                                        setSettings({
                                            ...settings,
                                            dedupVideoMaxMb: v === '' ? undefined : parseInt(v, 10),
                                        });
                                    }}
                                >
                                    <option value="">200 MB (default)</option>
                                    <option value="500">500 MB</option>
                                    <option value="1000">1 GB</option>
                                    <option value="2000">2 GB</option>
                                    <option value="0">Illimitato (controlla tutti)</option>
                                </select>
                                <p className="text-[10px] text-slate-500 italic mt-1.5">
                                    Oltre questa dimensione i <b>video</b> non vengono controllati
                                    per i duplicati (due upload dello stesso file non verranno
                                    segnalati). Le <b>foto</b> sono sempre deduplicate a prescindere.
                                    "Illimitato" controlla ogni video: più sicuro contro i doppioni,
                                    ma aggiunge qualche secondo di elaborazione al caricamento dei
                                    file molto grandi. Vuoto = 200 MB (default).
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Permessi: deroga al responsabile per eliminare direttamente i media */}
                    <div className="lg:col-span-2 bg-white border border-slate-100 rounded-2xl p-6 space-y-5 shadow-sm">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg">
                            <Users size={20} className="text-indigo-500"/> Permessi
                        </h3>
                        <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl hover:bg-slate-50 transition-colors">
                            <input
                                type="checkbox"
                                className="mt-0.5 w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                checked={!!settings.responsabilePuoEliminareMediaDiretto}
                                onChange={e => setSettings({
                                    ...settings,
                                    responsabilePuoEliminareMediaDiretto: e.target.checked,
                                })}
                            />
                            <span className="flex-1">
                                <span className="block text-sm font-bold text-slate-800">
                                    Il responsabile può eliminare media direttamente
                                </span>
                                <span className="block text-[11px] text-slate-500 mt-1 leading-relaxed">
                                    Se disattivata (default), il responsabile non può cancellare foto/video
                                    senza l'OK del titolare: la richiesta finisce nell'hub Approvazioni come
                                    accade per il collaboratore. Attivala solo se ti fidi a delegare la
                                    cancellazione definitiva senza passaggio intermedio.
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl hover:bg-slate-50 transition-colors">
                            <input
                                type="checkbox"
                                className="mt-0.5 w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                checked={!!(settings as any).buoniPastoVisibiliCollaboratori}
                                onChange={e => setSettings({
                                    ...settings,
                                    buoniPastoVisibiliCollaboratori: e.target.checked,
                                } as any)}
                            />
                            <span className="flex-1">
                                <span className="block text-sm font-bold text-slate-800">
                                    Buoni pasto visibili al collaboratore
                                </span>
                                <span className="block text-[11px] text-slate-500 mt-1 leading-relaxed">
                                    Se disattivata (default), il collaboratore non vede in
                                    "Le mie spese" le righe "buono pasto" generate automaticamente
                                    in fase di approvazione. Restano comunque visibili a te in
                                    approvazione e nel report mensile. Indipendentemente da questo
                                    flag il collaboratore non può modificarle né eliminarle.
                                </span>
                            </span>
                        </label>
                    </div>

                    {/*
                      Privacy e retention: interruttore dell'anonimizzazione automatica dei dati
                      cestinati. Spento di default.
                    */}
                    <div className="lg:col-span-2 bg-white border border-amber-100 rounded-2xl p-6 space-y-5 shadow-sm">
                        <h3 className="font-bold text-gray-900 border-b pb-3 flex items-center gap-2 text-lg">
                            <ShieldAlert size={20} className="text-amber-500"/> Privacy & Retention (GDPR)
                            <button
                                type="button"
                                onClick={() => setShowGdprInfo(true)}
                                className="ml-auto flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
                                title="Scopri cosa fa il software con i tuoi dati"
                            >
                                <Info size={13}/> Cosa fa il software
                            </button>
                        </h3>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-[12px] text-amber-900 leading-relaxed">
                            <p className="font-bold mb-1 flex items-center gap-1.5">
                                <AlertTriangle size={14}/> Attenzione: operazione irreversibile
                            </p>
                            <p>
                                Se attivi questa opzione, ogni notte il sistema sovrascriverà con
                                "XXXXXX" i dati personali dei record che hai <b>cestinato</b> da più
                                del periodo di retention (default 6 mesi). L'operazione è
                                <b> definitiva</b>: i dati originali non saranno più recuperabili.
                                Sono coinvolti solo i record cestinati: mandati/turni/clienti attivi
                                non vengono mai toccati. Il flag disattivato (default) lascia i tuoi
                                dati cestinati inalterati per tempo indefinito, e sei tu a decidere
                                quando/se anonimizzarli.
                            </p>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl hover:bg-amber-50/40 transition-colors">
                            <input
                                type="checkbox"
                                className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                checked={!!(settings as any).gdprAutoAnonymizationEnabled}
                                onChange={e => setSettings({
                                    ...settings,
                                    gdprAutoAnonymizationEnabled: e.target.checked,
                                } as any)}
                            />
                            <span className="flex-1">
                                <span className="block text-sm font-bold text-slate-800">
                                    Attiva anonimizzazione automatica dei dati cestinati
                                </span>
                                <span className="block text-[11px] text-slate-500 mt-1 leading-relaxed">
                                    Se disattivata (default), nessun automatismo GDPR gira: i clienti
                                    cestinati, i preventivi rifiutati, i servizi fiduciari chiusi, gli
                                    utenti staff eliminati e l'hard-delete annuale restano <b>fermi</b>.
                                    Attivala solo se vuoi che il sistema anonimizzi in autonomia i dati
                                    cestinati dopo il periodo di retention.
                                </span>
                            </span>
                        </label>

                        <div className={(settings as any).gdprAutoAnonymizationEnabled ? '' : 'opacity-40 pointer-events-none'}>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Periodo di retention prima dell'anonimizzazione
                            </label>
                            <select
                                className="w-full md:w-1/2 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                                value={(settings as any).gdprRetentionDays ?? ''}
                                onChange={e => {
                                    const v = e.target.value;
                                    setSettings({
                                        ...settings,
                                        gdprRetentionDays: v === '' ? undefined : parseInt(v, 10),
                                    } as any);
                                }}
                            >
                                <option value="">180 giorni (default)</option>
                                <option value="0">Immediato (prossima notte)</option>
                                <option value="15">15 giorni</option>
                                <option value="30">30 giorni</option>
                                <option value="60">60 giorni</option>
                                <option value="90">90 giorni</option>
                                <option value="180">180 giorni</option>
                            </select>
                            <p className="text-[10px] text-slate-500 italic mt-1.5">
                                Dopo quanti giorni dal cestinamento il sistema anonimizza definitivamente
                                il record (Cliente / servizio fiduciario / preventivo temporaneo).
                                "Immediato" significa alla prossima esecuzione notturna (03:30).
                                Gli utenti staff hanno una retention propria (qui sotto).
                            </p>

                            <label className="block text-xs font-bold text-slate-500 uppercase mt-4 mb-1">
                                Elimina fisicamente l'anagrafica anonimizzata dopo
                            </label>
                            <select
                                className="w-full md:w-1/2 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                                value={(settings as any).gdprHardDeleteDaysAfterAnonymization ?? ''}
                                onChange={e => {
                                    const v = e.target.value;
                                    setSettings({
                                        ...settings,
                                        gdprHardDeleteDaysAfterAnonymization: v === '' ? undefined : parseInt(v, 10),
                                    } as any);
                                }}
                            >
                                <option value="">Mai (default)</option>
                                <option value="30">30 giorni</option>
                                <option value="90">90 giorni</option>
                                <option value="180">180 giorni</option>
                                <option value="365">365 giorni</option>
                            </select>
                            <p className="text-[10px] text-slate-500 italic mt-1.5">
                                Dopo l'anonimizzazione, il record anagrafico del cliente / servizio fiduciario
                                viene <b>eliminato fisicamente</b> dal database (cron notturno 03:50 / 03:55).
                                Incarichi, spese collaboratore, relazioni, turni e note del collaboratore
                                <b> restano</b> come record orfani per lo storico operativo e contabile.
                                "Mai" (default) mantiene il record anonimizzato indefinitamente.
                            </p>

                            <label className="block text-xs font-bold text-slate-500 uppercase mt-4 mb-1">
                                Retention utenti staff cancellati
                            </label>
                            <select
                                className="w-full md:w-1/2 p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                                value={(settings as any).gdprUserRetentionDays ?? ''}
                                onChange={e => {
                                    const v = e.target.value;
                                    setSettings({
                                        ...settings,
                                        gdprUserRetentionDays: v === '' ? undefined : parseInt(v, 10),
                                    } as any);
                                }}
                            >
                                <option value="">3650 giorni · 10 anni (default)</option>
                                <option value="0">Immediato (prossima notte)</option>
                                <option value="1825">5 anni</option>
                                <option value="2555">7 anni</option>
                                <option value="3650">10 anni</option>
                            </select>
                            <p className="text-[10px] text-slate-500 italic mt-1.5">
                                Alla cancellazione di un collaboratore/responsabile/admin, nome, cognome,
                                telefono ed email vengono <b>già anonimizzati subito</b> (il nome resta come
                                iniziali "M. R." per lo storico contabile). Questo valore governa solo il
                                cron di <b>rete di sicurezza</b> (03:45) sui record cancellati da versioni
                                precedenti. Il default 10 anni è un proxy prudenziale della prescrizione
                                ordinaria (art. 2946 c.c.), <b>non</b> un obbligo puntuale: valuta col tuo DPO.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-2 flex justify-end pt-4">
                        <button onClick={handleSaveSettings} disabled={isSaving} className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 shadow-lg active:scale-95 transition-all">
                            {isSaving ? <Loader2 className="animate-spin" size={24}/> : <Save size={24}/>} Salva Parametri Generali
                        </button>
                    </div>

                    {/* Test della connessione verso il server: solo ADMIN (endpoint riservati al titolare). */}
                    {isAdmin && <ConnessioneTestPanel/>}
                </div>
            )}

            {/* Tab documenti e stampe */}
            {activeTab === 'stampe' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4">

                    {/* Sezione 1: relazioni investigative */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 pb-2 border-b border-slate-200">
                            <div className="p-2 bg-blue-100 text-blue-600 rounded-xl"><FileText size={18}/></div>
                            <div>
                                <h3 className="font-bold text-slate-900 text-base">Relazioni Investigative</h3>
                                <p className="text-[11px] text-slate-400">Intestazione e piè di pagina delle relazioni</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Header relazione */}
                            <ImageSlot
                                label="Intestazione (Header)"
                                hint="2185×390px consigliato (18,5 × 3,3 cm) · appare in cima ad ogni pagina della relazione"
                                imageUrl={settings.headerImageUrl || (settings as any).reportHeaderUrl}
                                isUploading={isUploading === 'header'}
                                onUpload={e => handleImageUpload(e, 'header')}
                                onRemove={() => handleImageRemove('header')}
                            />
                            {/* Footer relazione */}
                            <ImageSlot
                                label="Piè di Pagina (Footer)"
                                hint="2185×272px consigliato (18,5 × 2,3 cm) · appare in fondo ad ogni pagina della relazione"
                                imageUrl={settings.footerImageUrl || (settings as any).reportFooterUrl}
                                isUploading={isUploading === 'footer'}
                                onUpload={e => handleImageUpload(e, 'footer')}
                                onRemove={() => handleImageRemove('footer')}
                            />
                        </div>

                        {/* Timbro relazione: immagine di firma/timbro in basso a destra nell'ultima pagina. */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <ImageSlot
                                label="Timbro Agenzia"
                                hint="circa 680×680px consigliato (PNG, ritagliato stretto) · stampato largo fino a 5,7 cm in basso a destra dell'ultima pagina della relazione"
                                imageUrl={(settings as any).timbroUrl}
                                isUploading={isUploading === 'timbro'}
                                onUpload={e => handleImageUpload(e, 'timbro')}
                                onRemove={() => handleImageRemove('timbro')}
                            />
                        </div>

                        {/* Testo HTML opzionale per la relazione */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Type size={12}/> Intestazione HTML (alternativa a immagine)</label>
                                <textarea className="w-full h-28 p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-xs bg-slate-50"
                                          placeholder="<div style='text-align:center; font-family:Arial'>...</div>"
                                          value={settings.headerReportInvestigativo}
                                          onChange={e => setSettings({...settings, headerReportInvestigativo: e.target.value})} />
                                <p className="text-[9px] text-slate-400 italic">Usato solo se non è caricata un'immagine header. Variabili: [CLIENTE] [PRATICA] [DATA]</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Type size={12}/> Piè di Pagina HTML (alternativa a immagine)</label>
                                <textarea className="w-full h-28 p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-xs bg-slate-50"
                                          value={settings.footerReportInvestigativo}
                                          onChange={e => setSettings({...settings, footerReportInvestigativo: e.target.value})} />
                            </div>
                        </div>
                    </div>

                    {/* Sezione 2: mandati e preventivi */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 pb-2 border-b border-slate-200">
                            <div className="p-2 bg-amber-100 text-amber-600 rounded-xl"><Scale size={18}/></div>
                            <div>
                                <h3 className="font-bold text-slate-900 text-base">Mandati e Preventivi</h3>
                                <p className="text-[11px] text-slate-400">Header e footer usati nei PDF di mandato, preventivo e contabilità</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            {/* Header mandato */}
                            <ImageSlot
                                label="Intestazione Mandati/Preventivi"
                                hint="2185×390px consigliato (18,5 × 3,3 cm) · appare in cima ai PDF di mandato e preventivo"
                                imageUrl={(settings as any).mandatoHeaderImageUrl || (settings as any).mandatoHeaderUrl}
                                isUploading={isUploading === 'mandato-header'}
                                onUpload={e => handleImageUpload(e, 'mandato-header')}
                                onRemove={() => handleImageRemove('mandato-header')}
                                accentColor="amber"
                            />
                            {/*
                              Il footer mandato/preventivo non è usato nel layout del mandato, quindi non
                              lo mostriamo. `mandatoFooterImageUrl` resta nello state e nel DTO per
                              compatibilità con i payload esistenti.
                            */}
                        </div>
                    </div>

                    {/* Anteprima A4 */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                            <Layout size={16} className="text-slate-400"/>
                            <span className="text-sm font-bold text-slate-600">Anteprima Layout Pagina</span>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Anteprima relazione */}
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Relazione Investigativa</p>
                                <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden shadow-md" style={{aspectRatio: '1/1.414'}}>
                                    <div className="w-full border-b border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden" style={{height: '15%'}}>
                                        {(settings.headerImageUrl || (settings as any).reportHeaderUrl)
                                            ? <img src={settings.headerImageUrl || (settings as any).reportHeaderUrl} className="h-full w-full object-contain" alt="header"/>
                                            : <span className="text-[9px] text-slate-400 font-bold uppercase">{settings.headerReportInvestigativo ? 'HTML Header' : 'Nessuna intestazione'}</span>}
                                    </div>
                                    <div className="flex-1 p-4 flex flex-col gap-1.5" style={{height: '70%'}}>
                                        {[80, 100, 60, 90, 75, 100, 45].map((w, i) => (
                                            <div key={i} className="h-1.5 rounded-full bg-slate-100" style={{width: `${w}%`}}/>
                                        ))}
                                    </div>
                                    <div className="w-full border-t border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden" style={{height: '10%'}}>
                                        {(settings.footerImageUrl || (settings as any).reportFooterUrl)
                                            ? <img src={settings.footerImageUrl || (settings as any).reportFooterUrl} className="h-full w-full object-contain" alt="footer"/>
                                            : <span className="text-[9px] text-slate-400 font-bold uppercase">{settings.footerReportInvestigativo ? 'HTML Footer' : 'Nessun piè di pagina'}</span>}
                                    </div>
                                </div>
                            </div>
                            {/* Anteprima mandato: solo header e corpo, come il PDF reale. */}
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Mandato / Preventivo</p>
                                <div className="bg-white border-2 border-amber-100 rounded-xl overflow-hidden shadow-md" style={{aspectRatio: '1/1.414'}}>
                                    <div className="w-full border-b border-amber-100 bg-amber-50/50 flex items-center justify-center overflow-hidden" style={{height: '15%'}}>
                                        {((settings as any).mandatoHeaderImageUrl || (settings as any).mandatoHeaderUrl)
                                            ? <img src={(settings as any).mandatoHeaderImageUrl || (settings as any).mandatoHeaderUrl} className="h-full w-full object-contain" alt="header mandato"/>
                                            : <span className="text-[9px] text-amber-400 font-bold uppercase">Nessuna intestazione</span>}
                                    </div>
                                    <div className="p-4 flex flex-col gap-1.5" style={{height: '85%'}}>
                                        {[70, 100, 55, 85, 100, 40, 90].map((w, i) => (
                                            <div key={i} className="h-1.5 rounded-full bg-amber-50" style={{width: `${w}%`}}/>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sezione 3: firma digitale - informativa privacy */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 pb-2 border-b border-slate-200">
                            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
                                <Key size={18}/>
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900 text-base">Firma Digitale — Informativa Privacy</h3>
                                <p className="text-[11px] text-slate-400">
                                    Carica il PDF dell'Informativa Privacy. Sarà salvato insieme al Mandato.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">

                            {/* Sorgente: UPLOAD o AUTO */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className={`p-4 border rounded-xl cursor-pointer transition-all flex items-start gap-3 ${settings.privacyPolicyMode !== 'AUTO' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 shadow-sm' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input
                                        type="radio"
                                        name="privacyPolicyMode"
                                        className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                                        checked={settings.privacyPolicyMode !== 'AUTO'}
                                        onChange={() => setSettings({ ...settings, privacyPolicyMode: 'UPLOAD' })}
                                    />
                                    <div className="min-w-0">
                                        <div className="font-bold text-sm text-slate-800 flex items-center gap-1.5"><UploadCloud size={14}/> PDF Personalizzato</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">Usa un file PDF caricato manualmente.</div>
                                    </div>
                                </label>
                                <label className={`p-4 border rounded-xl cursor-pointer transition-all flex items-start gap-3 ${settings.privacyPolicyMode === 'AUTO' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 shadow-sm' : 'border-slate-200 hover:bg-slate-50'}`}>
                                    <input
                                        type="radio"
                                        name="privacyPolicyMode"
                                        className="mt-1 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                                        checked={settings.privacyPolicyMode === 'AUTO'}
                                        onChange={() => setSettings({ ...settings, privacyPolicyMode: 'AUTO' })}
                                    />
                                    <div className="min-w-0">
                                        <div className="font-bold text-sm text-slate-800 flex items-center gap-1.5"><Sparkles size={14}/> Generata dalla piattaforma</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">Modello standard popolato con i dati dell'agenzia.</div>
                                    </div>
                                </label>
                            </div>

                            {/* Pannello AUTO */}
                            {settings.privacyPolicyMode === 'AUTO' ? (
                                <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="p-2.5 bg-indigo-100 rounded-xl">
                                            <Sparkles size={18} className="text-indigo-600"/>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-800 text-sm">Informativa generata automaticamente</p>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                                Compilata in tempo reale con ragione sociale, P. IVA, sede e contatti dell'agenzia.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handlePreviewGeneratedPrivacy}
                                        disabled={privacyPreviewLoading}
                                        className="px-3 py-2 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                                    >
                                        {privacyPreviewLoading ? <Loader2 size={13} className="animate-spin"/> : <Eye size={13}/>}
                                        Anteprima PDF
                                    </button>
                                </div>
                            ) : (
                                /* Pannello UPLOAD */
                                (settings as any).privacyPolicyUrl ? (
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-indigo-50 rounded-xl">
                                                <FileText size={24} className="text-indigo-600"/>
                                            </div>
                                            <div>
                                                <p className="font-bold text-slate-800 text-sm">Informativa Privacy caricata ✓</p>
                                                <p className="text-[11px] text-slate-400 mt-0.5">
                                                    Il file è salvato e verrà usato per la firma digitale
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <a
                                                href={(settings as any).privacyPolicyUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                                            >
                                                Visualizza
                                            </a>
                                            <label className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer flex items-center gap-1.5">
                                                {isUploading === 'privacy-policy'
                                                    ? <Loader2 size={13} className="animate-spin"/>
                                                    : <UploadCloud size={13}/>}
                                                Sostituisci PDF
                                                <input type="file" className="hidden" accept="application/pdf" onChange={handlePrivacyUpload}/>
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-indigo-200 bg-indigo-50 rounded-xl cursor-pointer hover:bg-indigo-100 transition-colors">
                                        {isUploading === 'privacy-policy' ? (
                                            <Loader2 size={32} className="animate-spin text-indigo-500"/>
                                        ) : (
                                            <UploadCloud size={32} className="text-indigo-400"/>
                                        )}
                                        <div className="text-center">
                                            <p className="font-bold text-sm text-indigo-700">Carica PDF Informativa Privacy</p>
                                            <p className="text-[11px] text-indigo-400 mt-0.5">
                                                Solo file .pdf · Sarà inviato come secondo documento di firma
                                            </p>
                                        </div>
                                        <input type="file" className="hidden" accept="application/pdf" onChange={handlePrivacyUpload}/>
                                    </label>
                                )
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button onClick={handleSaveSettings} disabled={isSaving} className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 shadow-lg active:scale-95 transition-all">
                            {isSaving ? <Loader2 className="animate-spin" size={24}/> : <Save size={24}/>} Salva Configurazione Stampe
                        </button>
                    </div>
                </div>
            )}

            {/* Tab figure professionali */}
            {activeTab === 'figure' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                    <div className="bg-indigo-50 border border-indigo-200 p-5 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h2 className="font-bold text-indigo-900 text-lg flex items-center gap-2"><Users size={20}/> Qualifiche e Tariffe Orarie</h2>
                            <p className="text-sm text-indigo-700">Configura le figure professionali e le loro tariffe orarie dedicate.</p>
                        </div>
                        <button onClick={() => setEditingFigura({ nome: '', descrizione: '', tariffa: 0, prezzoCliente: 0 })} className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-bold shadow-md hover:bg-indigo-700 flex items-center gap-2 whitespace-nowrap"><Plus size={18}/> Nuova Figura</button>
                    </div>

                    {editingFigura && (
                        <div className="bg-white border-2 border-indigo-100 rounded-xl p-6 shadow-lg animate-in zoom-in duration-200">
                            <h3 className="font-bold text-gray-900 mb-4">{editingFigura.id ? "Modifica Figura" : "Nuova Figura"}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Qualifica / Ruolo</label>
                                    <input type="text" className="w-full p-3 border rounded-lg font-bold" value={editingFigura.nome} onChange={e => setEditingFigura({...editingFigura, nome: e.target.value})} placeholder="Es. Analista in Digital Forensic"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prezzo Cliente (€/h)</label>
                                    <NumberInput
                                           min={0} step="0.5" className="w-full p-3 border border-blue-200 bg-blue-50 rounded-lg text-right font-bold" value={editingFigura.prezzoCliente ?? 0} onChange={e => setEditingFigura({...editingFigura, prezzoCliente: parseFloat(e.target.value) || 0})} />
                                    <p className="text-[9px] text-slate-400 mt-0.5">Usato nei preventivi</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Costo Interno (€/h)</label>
                                    <NumberInput
                                           min={0} step="0.5" className="w-full p-3 border border-indigo-200 bg-indigo-50 rounded-lg text-right font-bold" value={editingFigura.tariffa} onChange={e => setEditingFigura({...editingFigura, tariffa: parseFloat(e.target.value) || 0})} />
                                    <p className="text-[9px] text-slate-400 mt-0.5">Costo collaboratore</p>
                                </div>
                                <div className="md:col-span-4">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrizione</label>
                                    <textarea className="w-full p-3 border rounded-lg h-20 resize-none" value={editingFigura.descrizione} onChange={e => setEditingFigura({...editingFigura, descrizione: e.target.value})} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                                <button onClick={() => setEditingFigura(null)} className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-lg font-bold">Annulla</button>
                                <button onClick={handleSaveFigura} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-bold flex items-center gap-2"><Check size={18}/> Salva Figura</button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase border-b">
                            <tr><th className="p-4">Qualifica</th><th className="p-4">Descrizione</th><th className="p-4 text-right">Prezzo Cliente</th><th className="p-4 text-right">Costo Interno</th><th className="p-4 text-center">Azioni</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {figure.map(fig => (
                                <tr key={fig.id} className="hover:bg-indigo-50/50 group transition-colors">
                                    <td className="p-4 font-bold text-slate-900">{fig.nome}</td>
                                    <td className="p-4 text-slate-600">{fig.descrizione}</td>
                                    <td className="p-4 text-right font-bold text-blue-600">€ {(fig.prezzoCliente ?? 0).toFixed(2)}</td>
                                    <td className="p-4 text-right font-bold text-indigo-600">€ {(fig.tariffa ?? 0).toFixed(2)}</td>
                                    <td className="p-4 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => setEditingFigura(fig)} className="p-2 bg-white border rounded-lg text-indigo-600 shadow-sm"><Edit2 size={16}/></button>
                                        <button onClick={() => handleDeleteFigura(fig.id!)} className="p-2 bg-white border rounded-lg text-rose-500 shadow-sm"><Trash2 size={16}/></button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tab listino servizi */}
            {activeTab === 'listino' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
                    <div className="bg-blue-50 border border-blue-200 p-5 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h2 className="font-bold text-blue-900 text-lg">Listino Attività Investigative</h2>
                            <p className="text-sm text-blue-700">Inserisci qui i servizi standard per preventivi e mandati.</p>
                        </div>
                        <button onClick={() => setEditingActivity({ name: '', description: '', defaultPrice: 0 })} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-bold shadow-md hover:bg-blue-700 flex items-center gap-2 whitespace-nowrap"><Plus size={18}/> Nuovo Servizio</button>
                    </div>

                    {editingActivity && (
                        <div className="bg-white border-2 border-blue-100 rounded-xl p-6 shadow-lg animate-in zoom-in duration-200">
                            <h3 className="font-bold text-gray-900 mb-4">{editingActivity.id ? "Modifica Voce" : "Nuova Voce"}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome Servizio</label>
                                    <input type="text" className="w-full p-3 border rounded-lg font-bold" value={editingActivity.name} onChange={e => setEditingActivity({...editingActivity, name: e.target.value})} placeholder="Es. OSINT"/>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prezzo Base (€)</label>
                                    <NumberInput
                                           min={0} className="w-full p-3 border rounded-lg text-right font-bold" value={editingActivity.defaultPrice} onChange={e => setEditingActivity({...editingActivity, defaultPrice: parseFloat(e.target.value)})} />
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrizione</label>
                                    <textarea className="w-full p-3 border rounded-lg h-20 resize-none" value={editingActivity.description} onChange={e => setEditingActivity({...editingActivity, description: e.target.value})} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                                <button onClick={() => setEditingActivity(null)} className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-lg font-bold">Annulla</button>
                                <button onClick={handleSaveActivity} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold flex items-center gap-2"><Check size={18}/> Salva Voce</button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase border-b">
                            <tr><th className="p-4">Servizio</th><th className="p-4">Descrizione</th><th className="p-4 text-right">Prezzo Base</th><th className="p-4 text-center">Azioni</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                            {activities.map(act => (
                                <tr key={act.id} className="hover:bg-blue-50/50 group transition-colors">
                                    <td className="p-4 font-bold text-slate-900">{act.name}</td>
                                    <td className="p-4 text-slate-600 truncate max-w-xs">{act.description}</td>
                                    <td className="p-4 text-right font-bold text-blue-600">€ {act.defaultPrice?.toFixed(2)}</td>
                                    <td className="p-4 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => setEditingActivity(act)} className="p-2 bg-white border rounded-lg text-blue-600 shadow-sm"><Edit2 size={16}/></button>
                                        <button onClick={() => handleDeleteActivity(act.id!)} className="p-2 bg-white border rounded-lg text-rose-500 shadow-sm"><Trash2 size={16}/></button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modale informativa "cosa fa il software" */}
            {showGdprInfo && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={() => setShowGdprInfo(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-5 py-3.5 border-b border-slate-200 bg-amber-50">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="p-1.5 bg-amber-100 text-amber-600 rounded-lg shrink-0">
                                    <ShieldAlert size={16}/>
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-black text-slate-900">Cosa fa il software con i tuoi dati</h3>
                                    <p className="text-[11px] text-slate-500 truncate">Privacy & Retention · trasparenza operativa</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowGdprInfo(false)}
                                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                                title="Chiudi"
                            >
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-5 text-sm text-slate-700 leading-relaxed">
                            <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded-r-lg p-4">
                                <p className="font-bold text-emerald-900 mb-1">Regola generale</p>
                                <p className="text-[13px] text-emerald-900">
                                    Nessuna iniziativa irreversibile senza il tuo consenso esplicito.
                                    Nella configurazione di default (opzione qui sotto disattivata),
                                    <b> il software non elimina né anonimizza niente</b>: i dati
                                    cestinati restano nel database nella loro forma originale.
                                </p>
                            </div>

                            <section>
                                <h4 className="font-bold text-slate-900 text-[13px] mb-2 flex items-center gap-1.5">
                                    <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[11px] flex items-center justify-center">1</span>
                                    Cosa succede se l'opzione qui sotto è disattivata (default)
                                </h4>
                                <ul className="list-disc list-inside space-y-1 text-[13px] pl-2">
                                    <li>Mandati e pratiche concluse: <b>intatti</b>, a tempo indefinito.</li>
                                    <li>Turni terminati: <b>intatti</b>.</li>
                                    <li>Clienti attivi (anche archiviati): <b>intatti</b>.</li>
                                    <li>Preventivi in qualsiasi stato: <b>intatti</b>.</li>
                                    <li>Utenti staff attivi: <b>intatti</b>.</li>
                                </ul>
                                <p className="text-[12px] italic text-slate-500 mt-2">
                                    <b>Cestinare ≠ anonimizzare</b>: cestinare mette il record in stato
                                    "nascosto dalle liste normali", il dato originale resta integro nel database.
                                </p>
                            </section>

                            <section>
                                <h4 className="font-bold text-slate-900 text-[13px] mb-2 flex items-center gap-1.5">
                                    <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-700 text-[11px] flex items-center justify-center">2</span>
                                    Cosa succede se attivi l'opzione qui sotto
                                </h4>
                                <p className="text-[13px] mb-2">
                                    Ogni notte il sistema esegue 5 operazioni automatiche
                                    <b> solo sui record che tu hai cestinato</b> e sono passati oltre il periodo di retention:
                                </p>
                                <div className="space-y-2">
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                        <p className="font-bold text-[12px] text-slate-800">03:30 — Clienti cestinati</p>
                                        <p className="text-[12px] text-slate-600">Nome, cognome, ragione sociale, P.IVA, CF, PEC, email, telefono, indirizzi, dati documento, referente, "per conto terzi" → sovrascritti con <code className="bg-slate-200 px-1 rounded text-[10px]">XXXXXX</code>. <b>Solo l'anagrafica del cliente</b>: mandati, incarichi, relazioni e dati del soggetto investigato NON vengono toccati (quelli si anonimizzano solo eliminando il mandato).</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                        <p className="font-bold text-[12px] text-slate-800">03:35 — Preventivi rifiutati/cestinati</p>
                                        <p className="text-[12px] text-slate-600">Solo preventivi con "cliente temporaneo" (mai agganciati a un cliente vero), rifiutati/annullati da più della retention. Sovrascritti nome/email/telefono del richiedente.</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                        <p className="font-bold text-[12px] text-slate-800">03:40 — Servizi fiduciari cestinati</p>
                                        <p className="text-[12px] text-slate-600">Nome postazione, indirizzo, movimenti contabili. Cascata sui turni e sulle note dei turni.</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                        <p className="font-bold text-[12px] text-slate-800">03:45 — Utenti staff cestinati da 10 anni</p>
                                        <p className="text-[12px] text-slate-600">Nome (→ iniziali), cognome, telefono ed email anonimizzati <b>subito</b> alla cancellazione. Il record utente resta nel DB per non lasciare incarichi orfani nei report. Retention del cron di sicurezza configurabile (default 10 anni).</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                        <p className="font-bold text-[12px] text-slate-800">1° gennaio 04:00 — Hard-delete annuale</p>
                                        <p className="text-[12px] text-slate-600">Cancella <b>fisicamente</b> dal DB i clienti e i servizi fiduciari già anonimizzati da oltre 10 anni. Housekeeping puro (niente dati residui).</p>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg p-4">
                                <p className="font-bold text-orange-900 mb-1 flex items-center gap-1.5">
                                    <AlertTriangle size={14}/> Cancellazione manuale di un mandato/servizio — è un'altra cosa
                                </p>
                                <p className="text-[13px] text-orange-900">
                                    Le operazioni qui sopra sono <b>automatiche</b> e dipendono da questo switch.
                                    Cestinare a mano un <b>mandato</b> o un <b>servizio fiduciario</b> è invece
                                    <b> immediato e definitivo, a prescindere da questa impostazione</b>: al click il
                                    sistema anonimizza subito il soggetto investigato (nome → iniziali; dati, luogo,
                                    relazioni e report azzerati) e <b>cancella fisicamente da S3</b> foto e video delle
                                    prove, scontrini, foto start/stop dei turni e i PDF del mandato/soggetto. Non è
                                    recuperabile: elimina un mandato/servizio solo dopo aver verificato e scaricato ciò
                                    che ti serve.
                                </p>
                            </section>

                            <section className="bg-red-50 border border-red-200 rounded-xl p-4">
                                <h4 className="font-bold text-red-900 text-[13px] mb-1.5 flex items-center gap-1.5">
                                    <AlertTriangle size={14}/> Cosa NON viene mai fatto in automatico
                                </h4>
                                <ul className="list-disc list-inside space-y-1 text-[12px] text-red-900 pl-2">
                                    <li>Record <b>non cestinati</b> (attivi o conclusi): mai toccati.</li>
                                    <li>Fatture, proforma, dati contabili in senso stretto: <b>mai</b> toccati (conservazione fiscale 10 anni).</li>
                                    <li>Nome del mandato ("Mandato di Rossi Mario"): mai toccato, resta come chiave storica.</li>
                                    <li>Storico operativo del collaboratore (incarichi/turni/relazioni): mai eliminato in autonomia.</li>
                                    <li>Nessun dato PII viene mai spedito a terze parti in autonomia.</li>
                                </ul>
                            </section>

                            <section>
                                <h4 className="font-bold text-slate-900 text-[13px] mb-2">Cosa NON è recuperabile una volta anonimizzato</h4>
                                <p className="text-[13px]">
                                    L'anonimizzazione sovrascrive i dati <b>in place</b>. Non esiste
                                    uno "storico" applicativo dei valori originali. Se anonimizzi un
                                    cliente per errore, il record esiste ancora ma nome/email/telefono
                                    sono definitivamente perduti (a meno di un backup DB pregresso —
                                    parla con il tuo amministratore di sistema per la retention dei
                                    backup).
                                </p>
                            </section>

                        </div>

                        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
                            <button
                                onClick={() => setShowGdprInfo(false)}
                                className="px-5 py-2 text-xs font-bold text-white bg-slate-700 hover:bg-slate-800 rounded-lg transition-colors"
                            >
                                Ho capito
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale anteprima informativa privacy generata */}
            {privacyPreviewUrl && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={closePrivacyPreview}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-5 py-3.5 border-b border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg shrink-0">
                                    <FileText size={16}/>
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-black text-slate-900">Anteprima Informativa Privacy</h3>
                                    <p className="text-[11px] text-slate-500 truncate">Generata con i dati dell'agenzia · {settings.agencyName || '—'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={downloadPrivacyPreview}
                                    className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-1.5 transition-colors"
                                >
                                    <UploadCloud size={13} className="rotate-180"/> Scarica
                                </button>
                                <button
                                    onClick={closePrivacyPreview}
                                    className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                                    title="Chiudi"
                                >
                                    <X size={18}/>
                                </button>
                            </div>
                        </div>
                        <iframe
                            src={privacyPreviewUrl}
                            title="Anteprima Informativa Privacy"
                            className="flex-1 w-full bg-slate-100"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImpostazioniView;